import { Component, Inject } from "@angular/core";
import { FormGroup, FormControl } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { LxmMessage, LocalStorage } from "src/app/_helpers";
import { ActionHelper } from "src/app/_helpers/action";
import { ProductService } from "src/app/_services";
import { appSettings } from "src/app/app.settings";
import { LOCAL_STORAGE_KEY } from "src/app/config";
import { ConformityDeclarationType, UserAction } from "src/app/enum";
import { AppState } from "src/app/state/app.state";

@Component({
  selector: "conformity-declaration-dialog",
  host: { class: "dialog" },
  templateUrl: "./conformity-declaration.dialog.html"
})
export class ConformityDeclarationDialog {
  public formGroup: FormGroup;

  public languageOptions = appSettings.languages;
  public selectedProductIds = [];

  public actionLoading = {} as any;

  public ConformityDeclarationType = ConformityDeclarationType;

  public withResponse = false;
  public contactOptions = [];

  constructor(
    public dialogRef: MatDialogRef<ConformityDeclarationDialog>,
    private _productService: ProductService,
    private _msg: LxmMessage,
    private _action: ActionHelper,
    private appState: AppState,
    @Inject(MAT_DIALOG_DATA) public data
  ) {
    const { productIds, contactOptions, withResponse, selectedFormats } = this.data || {};
    this.selectedProductIds = productIds;
    this.withResponse = withResponse;
    this.contactOptions = contactOptions;

    const savedLanguageId = LocalStorage.getValue(
      LOCAL_STORAGE_KEY.CONFORMITY_DECLARATION_LANGUAGE
    );

    const initialLanguage =
      this.languageOptions.find((x) => x.id === savedLanguageId) ||
      this.languageOptions[0];

    const formats = [
      { name: "Excel", id: ConformityDeclarationType.Excel, icon: "excel" },
      { name: "PDF", id: ConformityDeclarationType.Pdf, icon: "pdf" },
      { name: "PDF", id: ConformityDeclarationType.PdfDetailed, icon: "pdf" },
      { name: "PDF", id: ConformityDeclarationType.PdfSpecification, icon: "pdf" }
    ];

    this.formatOptions = formats.filter(x => selectedFormats.includes(x.id));

    this.formGroup = new FormGroup({
      declarationType: new FormControl(this.formatOptions[0].id),
      language: new FormControl(initialLanguage),
      includeProductExpiration: new FormControl(
        LocalStorage.getValue(
          LOCAL_STORAGE_KEY.CONFORMITY_DECLARATION_INCLUDE_EXPIRATION
        ) ?? false
      ),
      recipients: new FormControl()
    });
  }

  public get showTooManyProductsWarning() {
    return this.selectedProductIds.length > 20;
  }

  public get canSendConformityDeclarations() {
    return this.appState.hasRight([UserAction.SendConformityDeclarations]) &&
      this.formGroup.value.declarationType !== ConformityDeclarationType.Excel
  }

  public get showProductExpirationOption() {
    return this.formGroup.value.declarationType !== ConformityDeclarationType.PdfDetailed &&
      this.formGroup.value.declarationType !== ConformityDeclarationType.PdfSpecification
  }

  public formatOptions = [];

  public saveLocalValue(value: any, formKey: string) {
    switch (formKey) {
      case "declarationType":
        LocalStorage.set(
          LOCAL_STORAGE_KEY.CONFORMITY_DECLARATION_TYPE,
          value.id
        );
        break;
      case "language":
        LocalStorage.set(
          LOCAL_STORAGE_KEY.CONFORMITY_DECLARATION_LANGUAGE,
          value.id
        );
        break;
      case "includeProductExpiration":
        LocalStorage.set(
          LOCAL_STORAGE_KEY.CONFORMITY_DECLARATION_INCLUDE_EXPIRATION,
          value.checked
        );
        break;
    }
  }

  public save() {
    const form = this.formGroup.value;

    const req: any = {
      declarationType: form.declarationType,
      language: form.language?.value,
      includeProductExpiration: form.includeProductExpiration,
      productIds: this.selectedProductIds
    };

    if (req.declarationType !== ConformityDeclarationType.Excel) {
      Object.assign(req, {
        recipients: form.recipients?.map((x) => x.email) ?? []
      });
    };

    const msg = req.recipients?.length
      ? this._action.createMessage(
        "cards.products.conformity_declaration_dialog.save_success_with_recipients",
        "global.generic_error",
        true
      )
      : this._action.createMessage(
        "cards.products.conformity_declaration_dialog.save_success",
        "global.generic_error",
        true
      );

    if (this.data.withResponse) {
      // action for product card declaration create with declaration dto as result
      this._productService
        .addConformityDeclaration(this.selectedProductIds[0], req)
        .result(
          this.formGroup,
          (res) => {
            this.closeDialog(res);
          },
          (e) => {
            console.error(e);
          },
          "save",
          this.actionLoading,
          msg
        );
    } else {
      // action for products list dialog - without result
      this._productService.createConformityDeclaration(req).result(
        this.formGroup,
        (res) => {
          this.closeDialog();
        },
        (e) => {
          console.error(e);
        },
        "save",
        this.actionLoading,
        msg
      );
    }
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public closeDialog(res = null) {
    this.dialogRef.close(res);
  }
}
